import { Platform } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bProductCatalogProducts } from '@cart-services/hooks/fp-b2b/use-fp-b2b-product-catalog-products'
import { ProjectCode } from '@constants'
import { useApiCampaignProductsList } from '@hooks-api/fortune-fastpass/use-api-campaign-products-list'
import { type CampaignsProduct, type CampaignsProduct2B } from '@hooks-api/types/dataModel/campaigns'
import { type Variation } from '@hooks-api/types/dataModel/products'
import { useHistory } from 'react-router-dom-v5'

const useMerchantProductApi = (campaignId: number, projectCode: ProjectCode, campaignCode: string) => {
  const { destructuredData: productsData } = useFpB2bProductCatalogProducts(campaignId, projectCode, campaignCode, Platform.MERCHANT)
  return { productsData }
}

const useCampaignProductApi = (projectCode: ProjectCode, campaignCode: string) => {
  const { destructuredData: productsData } = useApiCampaignProductsList({ campaign_code: campaignCode, project_code: projectCode })
  return { productsData }
}

interface BasicClickableHandlerProps {
  projectCode: ProjectCode
  productsData: CampaignsProduct | CampaignsProduct2B | undefined
  productClicked: Variation
  history: ReturnType<typeof useHistory>
  productCatalogUrl: string
  onToggle: () => void
  isCollapse: boolean
}

const basicClickableHandler = ({
  projectCode,
  productsData,
  productClicked,
  history,
  productCatalogUrl,
  onToggle,
  isCollapse,
}: BasicClickableHandlerProps) => {
  onToggle()
  history.push(productCatalogUrl)
  const products = productsData?.products
  if (products == null) return
  // use setTimeout here because products are generated based on tab. Timeout is used as the wait time of total UI rendering time.
  // if setTimeout is not used here, null is returned by getElementById
  // assume time of fetching and rendering per product is 20 ms.

  // helper function to delay the execution of the function
  const executeWithDelay = async (cb: () => void, delay: number) => await new Promise<void>(resolve =>
    setTimeout(() => {
      cb()
      resolve()
    }, delay),
  )

  /** 產品分類 element id */
  const categoryId = `${productClicked.project_code}-${productClicked.product_class}`
  /** 母產品 element id */
  const productId = `${productClicked.project_code}-${productClicked.title}`
  /** 母產品變化類型 element id */
  const productWeightAttributeId = `${productClicked.project_code}-${productClicked.title}-${productClicked.weight_attribute}`
  /** 子產品 sku element id */
  const productSkuId = `${productClicked.project_code}-${productClicked.sku}`

  const handleProductClick = async () => {
    checkProjectCode()
    await handleInitialScrollAndClick()
    await handleCollapseActions()
  }

  const checkProjectCode = () => {
    if (productClicked.project_code !== projectCode) {
      console.warn('should switch brand')
    }
  }

  const scrollToAndClick = (elementId: string, options: ScrollIntoViewOptions, shouldClick = false) => {
    const element = document.getElementById(elementId)
    element?.scrollIntoView(options)
    if (shouldClick) element?.click()
  }

  /** 展開母產品 */
  const handleInitialScrollAndClick = async () => {
    scrollToAndClick(categoryId, { behavior: 'smooth', block: 'nearest', inline: 'start' }, true)
    await executeWithDelay(() => {
      scrollToAndClick(productId, { behavior: 'smooth', block: 'center', inline: 'nearest' }, isCollapse)
    }, 300)
  }

  /** 切換母產品變化類型，並於 selected 的子產品加上閃爍效果 */
  const handleCollapseActions = async () => {
    await executeWithDelay(() => {
      scrollToAndClick(productWeightAttributeId, { behavior: 'smooth', block: 'center', inline: 'nearest' }, true)
    }, 100)

    await executeWithDelay(() => {
      const childProductElement = document.getElementById(productSkuId)
      childProductElement?.classList.add('search-product-selected')
    }, 100)
  }
  handleProductClick()
}

interface LegacySearcherConfig {
  useProductsData: () => { productsData: CampaignsProduct | CampaignsProduct2B | undefined }
  clickableHandler: (productsData: BasicClickableHandlerProps['productsData'], productClicked: Variation) => void
}

export const useLegacySearcherConfig = (projectCode: ProjectCode = ProjectCode.DCS, campaignCode: string, campaignId: number, onToggle: () => void): LegacySearcherConfig => {
  const history = useHistory()
  const pathSegments = window.location.pathname.split('/').filter(Boolean)
  const configs = {
    merchant: {
      useProductsData: () => useMerchantProductApi(campaignId, projectCode, campaignCode),
      clickableHandler: (productsData: BasicClickableHandlerProps['productsData'], productClicked: Variation) => basicClickableHandler({ projectCode, productsData, productClicked, history, productCatalogUrl: `/merchant/${campaignCode}/product-catalog`, onToggle, isCollapse: false }),
    },
    campaigns: {
      useProductsData: () => useCampaignProductApi(projectCode, campaignCode),
      // TODO: 需要改成可以切換品牌
      clickableHandler: (productsData: BasicClickableHandlerProps['productsData'], productClicked: Variation) => {
        basicClickableHandler({ projectCode, productsData, productClicked, history, productCatalogUrl: `/campaigns/${campaignCode}`, onToggle, isCollapse: true })
      },
    },
  }
  return configs[pathSegments[0] as keyof typeof configs]
}
